import {
  Avatar,
  AvatarFallback,
  Badge,
  Body,
  Button,
  Icon,
  RowCard,
  useToast,
} from "@elaniintc/goes-design-system";
import { useState } from "react";
import SignatureModal from "./signature-modal";
import SignatureDetailModal from "./signature-detail-modal";
import {
  useDownloadOriginalDocument,
  useDownloadSignedDocument,
  useGetSingRequest,
  useSignDocument,
  type ISignDocument,
} from "@/queries/process/signature.api";
import { useParams } from "react-router";
import { useAuth } from "@/contexts/auth-provider/useAuth";
import { useGetExecution } from "@/queries/flows/flows.api";
import { useGetFormalityRequestDetail } from "@/queries/formality-request";
import { FORM_ASSIGNMENTS_STATUS } from "@/types/entities/formality-request";
import { useInfo } from "../../hooks/use-info";
import { groupSignersByPosition } from "@/pages/procedures/components/signature/utils/groupBySignersPosition";
import type { Signer } from "./type";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";

const SignatureDetail = () => {
  const { hasPermission } = useAuthorization();
  const canViewFlow = hasPermission(Permissions.flows.read);
  const { isCancelled } = useInfo();
  const { showToast } = useToast();
  const params = useParams<{ procedureId: string; singRequestId: string }>();
  const { authUser } = useAuth();
  const [documentLoading, setDocumentLoading] = useState<
    ISignDocument | undefined
  >(undefined);
  const [documentSignedSelected, setDocumentSignedSelected] = useState<
    ISignDocument | undefined
  >(undefined);
  const [signingDocumentIds, setSigningDocumentIds] = useState<Set<string>>(
    new Set(),
  );
  const [modalState, setModalState] = useState<
    "confirmPin" | "showPinDetail" | "idle"
  >("idle");

  const { data: signDocuments, isLoading } = useGetSingRequest(
    params.procedureId!, //NOSONAR: params are guaranteed by the route
    params.singRequestId!, //NOSONAR: params are guaranteed by the route
    {
      relationships: [
        "nodeExecution",
        "signers",
        "requiredSigners",
        "optionalSigners",
        "signedBy",
        "signedDocuments",
        "signable.media",
        "signedDocuments.media",
      ],
    },
    5000,
  );

  const { data: formalityRequest } = useGetFormalityRequestDetail(
    params.procedureId ?? "",
  );

  const { data: nodesExecutions } = useGetExecution(
    formalityRequest?.flow_execution_id as string,
    undefined,
    undefined,
    canViewFlow,
  );

  const node = nodesExecutions?.data.node_executions.find(
    (x) => x.id === params.singRequestId,
  );

  const desc = node?.inputs.find((x) => x.name === "description");
  const isComplete = node?.status === FORM_ASSIGNMENTS_STATUS.COMPLETED;

  const { mutateAsync, isPending } = useSignDocument();

  const canSingDocument = (document: ISignDocument): boolean => {
    return document.signers.data
      .map((s) => s.email)
      .includes(authUser?.email ?? "");
  };

  const isSignedDocument = (document: ISignDocument): boolean => {
    const signerSlot = groupSignersByPosition(
      document.signers.data as unknown as Signer[],
    ).find(
      (slot) =>
        slot.backup?.email === authUser?.email ||
        slot.primary?.email === authUser?.email,
    );

    return !!document.signedDocuments.data.some((doc) => {
      const primaryId = signerSlot?.primary?.id;
      const backupId = signerSlot?.backup?.id;

      const isSigned = doc.status === "signed";

      const primaryMatch = signerSlot && doc.signed_by === primaryId;
      const backupMatch = signerSlot && doc.signed_by === backupId;

      return isSigned && (primaryMatch || backupMatch);
    });
  };

  const hasSingInProgress = (document: ISignDocument): boolean => {
    const { id } = document;

    return (
      document.signedDocuments.data.find((x) => x.sign_request_id === id)
        ?.status === "in_process"
    );
  };

  const [selectedDocument, setSelectedDocument] =
    useState<ISignDocument | null>(null);
  const handleClick = (document: ISignDocument) => {
    if (document.sign_type === "simple" || document.sign_type === "automatic") {
      handleSing(document);
      return;
    }
    setSelectedDocument(document);
    setModalState("confirmPin");
  };

  const handleCertifiedSing = (pin: {
    pin: string;
    user: string;
    password: string;
  }) => {
    if (selectedDocument) {
      handleSing(selectedDocument, pin);
    }
  };

  const handleShowDocumentDetail = (document: ISignDocument) => {
    setDocumentSignedSelected(document);
    setModalState("showPinDetail");
  };

  const bitsToMB = (bits: number): string => {
    return (bits / (8 * 1024)).toFixed(2);
  };

  const handleSing = async (
    document: ISignDocument,
    pin?: {
      pin: string;
      user: string;
      password: string;
    },
  ) => {
    try {
      setSigningDocumentIds((prev) => new Set([...prev, document.id]));
      await mutateAsync({
        sign_request_id: document.id,
        ...(pin
          ? {
              credentials: {
                username: pin.user,
                password: pin.password,
                pin: pin.pin,
              },
            }
          : {}),
      });
      handleCloseModal();
      showToast({
        title: "Firma de documento",
        description: "Documento en proceso de firma.",
        options: {
          type: "success",
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="action" shape="square">
                <Icon type="fas" name="bell" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
    } catch {
      showToast({
        title: "Error al Firmar documento",
        description:
          "EL documento no pudo ser firmado. Por favor vuelve a ingresar a la sección e inténtalo de nuevo.",
        options: {
          type: "error",
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="danger" shape="square">
                <Icon type="fas" name="bell-exclamation" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
    } finally {
      setSigningDocumentIds((prev) => {
        const next = new Set(prev);
        next.delete(document.id);
        return next;
      });
    }
  };

  const { mutateAsync: downloadOriginal, isPending: loadingOriginal } =
    useDownloadOriginalDocument();
  const { mutateAsync: downloadSigned, isPending: loadingSigned } =
    useDownloadSignedDocument();

  const loadingPreview = loadingOriginal || loadingSigned;

  const onDownload = async (document: ISignDocument) => {
    setDocumentLoading(document);
    const signedDocument =
      document?.latestSignedDocument?.status === "signed" &&
      document.latestSignedDocument;
    const fn = signedDocument ? downloadSigned : downloadOriginal;

    try {
      await fn({
        id: signedDocument ? signedDocument.id : document.id,
      });
    } finally {
      setDocumentLoading(undefined);
    }
  };

  const handleCloseModal = () => {
    setModalState("idle");
  };

  return (
    <div className="flex flex-col gap-3">
      {isLoading && (
        <Icon
          name="spinner-third"
          type="fas"
          className="animate-spin ml-2 text-on-disabled text-sm"
        />
      )}

      <Body>{desc?.value as string}</Body>

      {modalState === "confirmPin" && (
        <SignatureModal
          isLoading={isPending}
          isOpen={modalState === "confirmPin"}
          onClose={handleCloseModal}
          onAccept={(pin) => {
            handleCertifiedSing(pin);
          }}
        />
      )}

      {modalState === "showPinDetail" && (
        <SignatureDetailModal
          isOpen={modalState === "showPinDetail"}
          documentSignedSelected={documentSignedSelected}
          onClose={handleCloseModal}
        />
      )}

      <div className="flex flex-col gap-2 w-full">
        {signDocuments?.data &&
          signDocuments?.data?.length > 0 &&
          [...(signDocuments?.data || [])]
            .sort((a, b) => (a.filename || "").localeCompare(b.filename || ""))
            .map((document, i) => {
              const canSing = canSingDocument(document);
              const isSigned = isSignedDocument(document);
              const isInprogress = hasSingInProgress(document);

              let buttonText = "Firmar";
              if (isInprogress) {
                buttonText = "Firmando";
              } else if (isSigned || isComplete) {
                buttonText = "Firmado";
              }

              return (
                <div
                  key={`document-${i + 1}`}
                  className="grid grid-cols-[85%_auto] 3xl:grid-cols-[1fr_auto] gap-2 items-center"
                >
                  <button
                    type="button"
                    className="text-start w-full"
                    onClick={() => !loadingPreview && onDownload(document)}
                  >
                    <RowCard
                      title={
                        document?.latestSignedDocument?.file
                          ?.formatted_file_name ||
                        document?.latestSignedDocument?.file?.file_name ||
                        document?.signable?.file.formatted_file_name ||
                        document?.signable?.file.file_name ||
                        document.filename
                      }
                      subtitle=""
                      color="neutral"
                    >
                      <RowCard.Left>
                        <Avatar
                          size="xs"
                          shape="square"
                          data-testid="document-avatar"
                        >
                          <AvatarFallback shape="square" color="neutral">
                            <Icon name="file" type="fas" />
                          </AvatarFallback>
                        </Avatar>
                      </RowCard.Left>
                      <RowCard.Right>
                        <Badge shape="pill">
                          {bitsToMB(
                            document?.latestSignedDocument?.file.size ??
                              document?.signable?.file.size ??
                              0,
                          )}{" "}
                          KB
                        </Badge>

                        {loadingPreview &&
                          documentLoading?.id === document.id && (
                            <Icon
                              type="far"
                              name="spinner-third"
                              className="animate-spin text-on-disabled"
                            />
                          )}
                      </RowCard.Right>
                    </RowCard>
                  </button>
                  <div>
                    <Button
                      className="min-w-20"
                      disabled={
                        isCancelled ||
                        (!isComplete &&
                          (!canSing ||
                            isInprogress ||
                            signingDocumentIds.has(document.id)))
                      }
                      onClick={() =>
                        isSigned || isComplete
                          ? handleShowDocumentDetail(document)
                          : handleClick(document)
                      }
                      isLoading={signingDocumentIds.has(document.id)}
                      variant={isSigned || isComplete ? "quiet" : "secondary"}
                      size="sm"
                    >
                      {buttonText}
                    </Button>
                  </div>
                </div>
              );
            })}
      </div>
    </div>
  );
};

export default SignatureDetail;
